/**
 * Question World Module - QuestionBookmark Component
 * Bookmark toggle for questions
 */

import React, { useEffect, useMemo, useState } from 'react';
import { BookOutlined, CloseOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { instance } from '../../helpers/api';
import '../../styles/QuestionBookmark.css';

function QuestionBookmark({ question = {} }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(Boolean(question?.is_in_bookmark));
    const [bookmarkId, setBookmarkId] = useState(question?.bookmark_id || null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setIsBookmarked(Boolean(question?.is_in_bookmark));
        setBookmarkId(question?.bookmark_id || null);
    }, [question?._id, question?.is_in_bookmark, question?.bookmark_id]);

    const student = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('student') || '{}');
        } catch (error) {
            return {};
        }
    }, []);

    const studentId = student?.id || student?._id;

    const ensureContext = () => {
        if (!studentId) {
            message.error('Please login again to manage bookmarks.');
            return false;
        }
        if (!question?._id) {
            message.error('Question context missing.');
            return false;
        }
        return true;
    };

    const closeModal = () => setIsModalOpen(false);

    const handleAddBookmark = async () => {
        if (!ensureContext() || isBookmarked) return;
        setIsSubmitting(true);
        try {
            const payload = {
                question_id: question._id,
                student_id: studentId,
                date: new Date().toISOString()
            };
            const response = await instance.post('/student-question-bookmark', payload);
            const bookmark = response?.data?.data;
            setBookmarkId(bookmark?._id);
            setIsBookmarked(true);
            message.success('Question added to bookmark list.');
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to add bookmark.';
            message.error(title);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveBookmark = async () => {
        if (!bookmarkId || !ensureContext()) return;
        setIsSubmitting(true);
        try {
            await instance.delete(`/student-question-bookmark/${bookmarkId}`);
            setBookmarkId(null);
            setIsBookmarked(false);
            message.success('Question removed from bookmark list.');
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to remove bookmark.';
            message.error(title);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleBookmark = () => {
        if (isSubmitting) return;
        if (isBookmarked) {
            handleRemoveBookmark();
        } else {
            handleAddBookmark();
        }
    };

    return (
        <div className="question_bookmark_container">
            <span
                className={`question-action-icon bookmark_button mr-2 ${isBookmarked ? 'is-bookmarked' : ''}`}
                title={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
                onClick={handleToggleBookmark}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                    if (event.key === 'Enter') handleToggleBookmark();
                }}
            >
                <BookOutlined />
            </span>

            {isModalOpen && (
                <div className="question_bookmark_backdrop" onClick={closeModal} role="presentation">
                    <div
                        className="question_bookmark_content"
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <button type="button" className="bookmark_close_btn" onClick={closeModal} aria-label="Close">
                            <CloseOutlined />
                        </button>
                        <h3 className="question_bookmark_title">{question?.title || 'Bookmark question'}</h3>
                        <p className="question_bookmark_hint">
                            {isBookmarked
                                ? 'This question is already saved. You can remove it from your bookmark list.'
                                : 'Save this question to your bookmark list for quick access later.'}
                        </p>
                        <div className="question_bookmark_actions">
                            <button
                                type="button"
                                className="bookmark_action primary"
                                disabled={isSubmitting || isBookmarked}
                                onClick={handleAddBookmark}
                            >
                                {isBookmarked ? 'Already bookmarked' : 'Add to bookmark'}
                            </button>
                            <button
                                type="button"
                                className="bookmark_action danger"
                                disabled={isSubmitting || !isBookmarked}
                                onClick={handleRemoveBookmark}
                            >
                                Remove from bookmark
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QuestionBookmark;

